import { computed, Ref, ref } from '@vue/composition-api';
import { CurrentLocationStatus, UploadLocationsItem, LocationTypeValue } from '../types';
import { LocationType, genBulkUploadSupportCountry, genLocationTypeList, MAX_COUNT } from '../const';
import {
  AdgroupFormData,
  CampaignFormData,
  LocationTargetingSearchScene,
  LocationStatus,
  LocationInfo,
  AreaItem,
  AreaStatus,
} from '@tt4b/ttam-enums';
import {
  useRequest,
  useSearchLocationBffApi,
  useStdAdgroupIs,
  useAdgroupLocationAvailable,
  useSharedAreaStore,
  useAllowlist,
} from '@tt4b/ttam-hooks';
import { generateLocationTargetingAdContext, formatLocationItem } from '../utils';
import { useLocationTargetingDataSource } from './location-data-source';
const associateTags = ref([]);
const checkedResult = ref({});

export function useBulkUploadValidate(options: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const invalidLocationList = ref([]);
  const notSupportedLocationList = ref([]);
  const notMatchedLocationList = ref([]);
  const { adgroupData, campaignData } = options;
  const { isEmpty, isLoading, error, run } = useRequest(useSearchLocationBffApi, {} as any, {
    manual: true,
  });

  async function searchLocation({ keywords, scene, countryCodes, geoTypes }) {
    const locationTargetingAdContext = generateLocationTargetingAdContext({
      adgroupData: adgroupData.value,
      campaignData: campaignData.value,
    });
    const searchLocationParam = {
      keywords,
      scene,
      queryParams: {
        countryCodes,
        geoTypes,
      },
      pagination: {
        page: 0,
        limit: 5, // 限制匹配的个数为5个
      },
      adContext: locationTargetingAdContext,
    };
    try {
      const { result, resp_context: respContext } = await run(searchLocationParam);
      associateTags.value = [...associateTags.value, ...respContext.associate_tags];
      checkedResult.value = result;
      return true;
    } catch (err) {
      checkedResult.value = {};
      return false;
    }
  }

  /**
   * 设置输入location后的loading状态
   */
  const setInputLocationCheckingStatus = (locations, status) => {
    locations.forEach(location => {
      if (location.value) {
        location.readonly = status;
      }
    });
  };

  const setInputLocationData = (opt: {
    location: UploadLocationsItem;
    param: { status: CurrentLocationStatus; isError: boolean; info: LocationInfo };
  }) => {
    const {
      location,
      param: { status, isError, info },
    } = opt;
    location.status = status;
    location.info = info;
    location.isError = isError;
    location.checked = true;
    // 模糊匹配成功，将用户输入替换为正确的name
    if (!isError) {
      location.value = info.name;
    }
  };

  // 记录批量上传中invalid的location
  const handleInvalidLocationList = (infoList, location) => {
    const invalidLocationIndex = invalidLocationList.value.findIndex(item => item.inputId === location.inputId);
    if (infoList.length === 0) {
      // 当前location非法
      if (invalidLocationIndex === -1) {
        invalidLocationList.value.push(location);
        setInputLocationData({
          location,
          param: {
            status: CurrentLocationStatus.INVAILD,
            isError: true,
            info: {},
          },
        });
      }
    } else {
      if (invalidLocationIndex > -1) {
        invalidLocationList.value.splice(invalidLocationIndex, 1);
      }
    }
  };

  // 记录批量上传中unsupported的location
  const handleUnsupportedLocationList = (infoList, location) => {
    const disabledLocationList = infoList.filter(item => item.status_info.status === LocationStatus.DISABLED);

    const notSupportedLocationIndex = notSupportedLocationList.value.findIndex(
      item => item.inputId === location.inputId,
    );
    if (infoList.length > 0 && disabledLocationList.length === infoList.length) {
      // 当前location不支持
      if (notSupportedLocationIndex === -1) {
        notSupportedLocationList.value.push(location);
        setInputLocationData({
          location,
          param: {
            status: CurrentLocationStatus.NOT_SUPPORTED,
            isError: true,
            info: {},
          },
        });
      }
    } else {
      if (notSupportedLocationIndex > -1) {
        notSupportedLocationList.value.splice(notSupportedLocationIndex, 1);
      }
    }
  };

  const handleCheckLocationResult = (infoList, location) => {
    handleInvalidLocationList(infoList, location);
    handleUnsupportedLocationList(infoList, location);

    const enabledLocationList = infoList.filter(item => item.status_info.status === LocationStatus.ENABLED);
    const notMatchedLocationIndex = notMatchedLocationList.value.findIndex(item => item.inputId === location.inputId);
    if (enabledLocationList.length > 1) {
      // 除去不支持的，还有多个匹配
      if (notMatchedLocationIndex === -1) {
        notMatchedLocationList.value.push(location);
        setInputLocationData({
          location,
          param: {
            status: CurrentLocationStatus.NOT_MATCH,
            isError: true,
            info: {},
          },
        });
      }
      location.matchList = enabledLocationList;
    } else if (enabledLocationList.length === 1) {
      // valid && supported && matched
      if (notMatchedLocationIndex > -1) {
        notMatchedLocationList.value.splice(notMatchedLocationIndex, 1);
      }
      setInputLocationData({
        location,
        param: {
          status: CurrentLocationStatus.VAILD,
          isError: false,
          info: enabledLocationList[0],
        },
      });
    } else {
      if (notMatchedLocationIndex > -1) {
        notMatchedLocationList.value.splice(notMatchedLocationIndex, 1);
      }
    }
  };

  // 本地校验用户输入，若info信息跟用户输入一直则认为校验通过
  const localCheckLocation = locations => {
    if (locations.length === 1) {
      const location = locations[0];
      const locationInfo = location.info;

      if (locationInfo && locationInfo.name === location.value) {
        return true;
      }
    }

    return false;
  };

  const checkLocation = async (opt: {
    keywords: number[] | string[];
    locations: UploadLocationsItem[];
    locationType: LocationTypeValue;
    country: string;
  }) => {
    const { keywords, locations: originLocations, locationType, country } = opt;
    let locations = originLocations;
    if (!locations[locations.length - 1].value) {
      locations = locations.slice(0, locations.length - 1);
    }
    if (!locations.length) {
      return { refresh: false };
    }
    setInputLocationCheckingStatus(locations, true);
    // 单个校验时，若接口校验已通过，则不再调用接口
    const locationCheckStatus = localCheckLocation(locations);
    if (locationCheckStatus) {
      setInputLocationCheckingStatus(locations, false);
      return { refresh: false };
    }
    // 调用接口check用户输入
    const isCheckedComplate = await searchLocation({
      keywords,
      scene:
        locationType === LocationTypeValue.CITY
          ? LocationTargetingSearchScene.CITY_BULK
          : LocationTargetingSearchScene.ZIPECODE_BULK,
      countryCodes: [country],
      geoTypes: LocationType[locationType],
    });
    if (!isCheckedComplate) {
      return { refresh: true };
    }
    locations.forEach(location => {
      const { value: keyword } = location;
      const infoList = checkedResult.value[keyword] || [];
      handleCheckLocationResult(infoList, location);
    });
    setInputLocationCheckingStatus(locations, false);
    return { refresh: false };
  };

  const errorList = {
    [CurrentLocationStatus.INVAILD]: invalidLocationList,
    [CurrentLocationStatus.NOT_SUPPORTED]: notSupportedLocationList,
    [CurrentLocationStatus.NOT_MATCH]: notMatchedLocationList,
  };

  const resetErrorList = (opt: { status?: CurrentLocationStatus; id?: number; isAll?: boolean }) => {
    const { id, isAll, status } = opt;
    if (isAll) {
      Object.values(errorList).forEach(list => {
        list.value = [];
      });
      return;
    }

    const list = errorList[status];
    if (!list) {
      return;
    }

    if (id) {
      const index = list.value.findIndex(item => item.inputId === id);
      if (index > -1) {
        list.value.splice(index, 1);
      }
    } else {
      list.value = [];
    }
  };

  return {
    isEmpty,
    isLoading,
    error,
    checkLocation,
    invalidLocationList,
    notSupportedLocationList,
    notMatchedLocationList,
    resetErrorList,
    handleCheckLocationResult,
  };
}
export function usePropsSupportBulkUpload(options: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  availableAreaList: Ref<AreaItem[]>;
}) {
  const { adgroupData, campaignData, availableAreaList } = options;
  // const { isOnlyPangle } = useStdAdgroupIs({ adgroupFormData: adgroupData, campaignFormData: campaignData });
  const { isZipcodeLocationAvailable, isAllCountrySupportBulkUploadCityAvailable } = useAdgroupLocationAvailable();
  const { isCASupportZipcodeWhite } = useAllowlist();
  const isSupportBulkUpload = computed(() => {
    const bulkUploadSupportCountry = genBulkUploadSupportCountry({
      isCASupportZipcode: isCASupportZipcodeWhite.value,
    });
    return (
      isZipcodeLocationAvailable.value &&
      (isAllCountrySupportBulkUploadCityAvailable.value ||
        bulkUploadSupportCountry.some(id => availableAreaList.value.some(item => item.id === id)))
    );
  });

  const bulkUploadEnabledMaxNumber = computed(() => {
    const { particleLocations, zipcodeIds } = adgroupData.value;
    return MAX_COUNT - particleLocations.length - zipcodeIds.length;
  });

  return {
    isSupportBulkUpload,
    bulkUploadEnabledMaxNumber,
  };
}

export function useLocationBulkUploadDataSource(options: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  availableAreaList: Ref<AreaItem[]>;
}) {
  const { adgroupData, campaignData, availableAreaList } = options;
  const { isSelectedIndustryType } = useStdAdgroupIs({ adgroupFormData: adgroupData, campaignFormData: campaignData });
  const { isHasTikTok } = useStdAdgroupIs({ adgroupFormData: adgroupData, campaignFormData: campaignData });

  const { getAreaMapInArea } = useSharedAreaStore();
  const { updateLocationData } = useLocationTargetingDataSource();
  const { isAllCountrySupportBulkUploadCityAvailable } = useAdgroupLocationAvailable();
  const { isCASupportZipcodeWhite } = useAllowlist();

  const availablCountryList = computed(
    () =>
      availableAreaList.value
        ?.filter(location => location?.status === AreaStatus.ACTIVE)
        ?.map(location => ({
          label: location?.translation,
          value: location?.code,
          id: location?.id,
        })) || [],
  );

  const zipcodeCountryList = computed(() => {
    const bulkUploadSupportCountry = genBulkUploadSupportCountry({
      isCASupportZipcode: isCASupportZipcodeWhite.value && isHasTikTok.value,
    });

    return availablCountryList.value?.filter(country => bulkUploadSupportCountry.includes(country.id));
  });

  const cityCountryList = computed(() => {
    if (isAllCountrySupportBulkUploadCityAvailable.value) {
      return availablCountryList.value;
    } else {
      return zipcodeCountryList.value;
    }
  });

  const countryListMap = computed(() => {
    const _countryListMap = {};
    if (zipcodeCountryList.value?.length) {
      _countryListMap[LocationTypeValue.ZIP_CODE] = zipcodeCountryList.value;
    }

    if (cityCountryList.value?.length) {
      _countryListMap[LocationTypeValue.CITY] = cityCountryList.value;
    }
    return _countryListMap;
  });

  const locationTypeList = computed(() => {
    const _locationTypeList = genLocationTypeList().filter(type => {
      if (type.value === LocationTypeValue.CITY) {
        return cityCountryList.value?.length;
      }
      if (type.value === LocationTypeValue.ZIP_CODE) {
        return zipcodeCountryList.value?.length;
      }
      return false;
    });
    if (isSelectedIndustryType.value) {
      return _locationTypeList.filter(item => item.value !== LocationTypeValue.ZIP_CODE);
    }
    return _locationTypeList;
  });

  function handleLogUploadResult(locations) {
    const areaMap = getAreaMapInArea();
    const locationsInfo = locations.map(location => location?.info);
    const { formatResult: associateTagsFormatResult, formatResultMap: associateTagsFormatResultMap } =
      formatLocationItem({
        tags: associateTags.value,
        isNotNeedFormatAssociateTags: true,
      });

    const { formatResult: tagsFormatResult, formatResultMap: tagsFormatResultMap } = formatLocationItem({
      tags: locationsInfo,
      isNotNeedFormatAssociateTags: true,
    });

    const formatResult = [...tagsFormatResult, ...associateTagsFormatResult];
    const formatResultMap = Object.assign({}, tagsFormatResultMap, associateTagsFormatResultMap);
    // 过滤不在areas_v2接口返回的item
    const formatResultNotInAreaV2 = formatResult.filter(item => areaMap && !areaMap[item.id]);
    const formatResultMapNotInAreaV2 = Object.values(formatResultMap)
      .filter(item => areaMap && !areaMap[item.id])
      .reduce((pre, cur) => ({ ...pre, [cur.id]: cur }), {});

    updateLocationData({
      list: formatResultNotInAreaV2,
      map: formatResultMapNotInAreaV2,
    });
  }
  return {
    handleLogUploadResult,
    countryListMap,
    locationTypeList,
  };
}
